import { Grid, Typography, Box, Stack, Divider } from '@mui/material';
import React, { ReactElement } from 'react';

import { theme } from '@app/theme';
import { Product } from '@app/types/catalog';
import {
  formatAreaRange,
  formatFloorPlan,
  formatPrice,
  formatPriceRange,
  getTransportMeanLabel,
  getTransportTimeLabel,
} from '@app/utils/format';

interface ProductCustomFieldsProps {
  product: Product;
}

export function PropertyDetails({
  product,
}: ProductCustomFieldsProps): ReactElement {
  const infoProperty = [
    {
      label: '土地面積',
      value: formatAreaRange(
        product.customFields.minLandArea,
        product.customFields.maxLandArea,
        product.customFields.landAreaRangeType
      ),
    },
    {
      label: '建物面積',
      value: formatAreaRange(
        product.customFields.minBuildingArea,
        product.customFields.maxBuildingArea,
        product.customFields.buildingAreaRangeType
      ),
    },
    {
      label: '交通',
      value: (
        <>
          {product.customFields.transportMainLine ||
          product.customFields.transportSub1Line ||
          product.customFields.transportSub2Line ? (
            <>
              {product.customFields.transportMainLine && (
                <Typography variant="body2">
                  {product.customFields.transportMainLine}「
                  {product.customFields.transportMainNearest}」まで
                  {getTransportMeanLabel(
                    product.customFields.transportMainMean
                  )}
                  {getTransportTimeLabel(
                    product.customFields.transportMainMean,
                    product.customFields.transportMainTimeMin,
                    product.customFields.transportMainTimeMax,
                    product.customFields.transportMainNote
                  )}
                </Typography>
              )}
              {product.customFields.transportSub1Line && (
                <Typography variant="body2">
                  {product.customFields.transportSub1Line}「
                  {product.customFields.transportSub1Nearest}」まで
                  {getTransportMeanLabel(
                    product.customFields.transportSub1Mean
                  )}
                  {getTransportTimeLabel(
                    product.customFields.transportSub1Mean,
                    product.customFields.transportSub1TimeMin,
                    product.customFields.transportSub1TimeMax,
                    product.customFields.transportSub1Note
                  )}
                </Typography>
              )}
              {product.customFields.transportSub2Line && (
                <Typography variant="body2">
                  {product.customFields.transportSub2Line}「
                  {product.customFields.transportSub2Nearest}」まで
                  {getTransportMeanLabel(
                    product.customFields.transportSub2Mean
                  )}
                  {getTransportTimeLabel(
                    product.customFields.transportSub2Mean,
                    product.customFields.transportSub2TimeMin,
                    product.customFields.transportSub2TimeMax,
                    product.customFields.transportSub2Note
                  )}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2">-</Typography>
          )}
        </>
      ),
    },
    {
      label: '住所',
      value: (
        <>
          {product.addressLine1 + product.addressLine2}
          {!!product.customFields.addressLine3HiddenFlag &&
            product.addressLine3}
        </>
      ),
    },
    {
      label: '販売戸数',
      value: product.customFields.salesHouseCount
        ? `${product.customFields.salesHouseCount}戸`
        : '未定',
    },
  ];

  return (
    <Box sx={{ width: 1 }}>
      <Box>
        <Typography
          color={theme.palette.blueAccent.main}
          fontSize={30}
          fontWeight="bold"
        >
          {formatPriceRange(
            product.customFields.minPrice
              ? Number(product.customFields.minPrice)
              : undefined,
            product.customFields.maxPrice
              ? Number(product.customFields.maxPrice)
              : undefined,
            formatPrice,
            product.customFields.priceRangeType
          )}
        </Typography>
        <Typography
          variant="body2"
          component={'span'}
          sx={{ ml: 2, whiteSpace: 'nowrap' }}
        >
          {formatFloorPlan(
            product.customFields.floorPlanRooms,
            product.customFields.floorPlanTypeMin,
            product.customFields.floorPlanTypeMax,
            product.customFields.floorPlanRangeType
          )}
        </Typography>
      </Box>
      <Divider sx={{ bgcolor: theme.palette.blueAccent.main, mt: 1 }} />
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={1}>
          {infoProperty.map((section, index) => (
            <Grid item xs={12} key={index}>
              <Stack direction="row" spacing={2} my={1}>
                <Typography color={theme.palette.blueAccent.main} width={100}>
                  {section.label}
                </Typography>
                <Box>{section.value}</Box>
              </Stack>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
